import { ThunkDispatch, EmptyObject, AnyAction } from '@reduxjs/toolkit';
import * as FileSaver from 'file-saver';
import JSZip from 'jszip';
import { includes, map, replace, split } from 'lodash';
import moment from 'moment';
import { Dispatch } from 'react';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { SalesExportAccountsPayableTxtResponseType } from '../../../../services/Sales/types';
import { salesListSlice } from '../../../../stores';

type HandledGlobusSaleRow = {
  paymentMethod: string;
  registerDate: string;
  sumPrice: number;
  areaId: number;
  areaName: string;
  areaColor: string;
  areaClientNumber: string;
  areaTypeRecipe: string;
  areaCostCenter: string;
  origin: string;
};

const exportGlobusAccountsPayableTxtZip = async (
  apiData: SalesExportAccountsPayableTxtResponseType,
  routes: {
    id: number;
    value: string;
    areaId: number;
  }[],
  dispatch: ThunkDispatch<EmptyObject & PersistPartial, undefined, AnyAction> &
    Dispatch<AnyAction>,
  worker: (
    apiData: SalesExportAccountsPayableTxtResponseType,
    routes: {
      id: number;
      value: string;
      areaId: number;
    }[],
    origin: string,
  ) => Promise<HandledGlobusSaleRow[]>,
  zipBasename: string,
): Promise<void> => {
  dispatch(salesListSlice.actions.setSalesExportPercentage(20));

  let handledApiData: HandledGlobusSaleRow[] = [];

  if (
    apiData.salesDriver &&
    apiData.salesUser &&
    apiData.areas &&
    apiData.txtSalesPayable
  ) {
    const handledSalesDriver = await worker(apiData, routes, 'driver');
    const handledSalesUser = await worker(apiData, routes, 'user');
    handledApiData = [...handledSalesDriver, ...handledSalesUser];
  }
  dispatch(salesListSlice.actions.setSalesExportPercentage(100));

  const zip = new JSZip();
  const consolidatedTexts: string[] = [];

  map(handledApiData, (item) => {
    let txt = createSalesExportTxtNewLine(apiData.txtSalesPayable, item);
    txt = txt + '\n';
    txt = txt + createSalesExportTxtNewLine(apiData.txtSalesPayable, item);

    consolidatedTexts.push(txt);

    zip.file(
      `${item.origin === 'driver' ? 'Motorista' : 'Operacional'} ${
        item.areaName
      } ${item.registerDate} ${item.paymentMethod}.txt`,
      txt,
    );
  });

  if (consolidatedTexts.length > 0) {
    zip.file('Consolidado.txt', consolidatedTexts.join('\n'));
  }

  const content = await zip.generateAsync({ type: 'blob' });

  FileSaver.saveAs(
    content,
    `${zipBasename}-${moment().format('yyyy-MM-DD_hh-mm')}` + '.zip',
  );
};

/**
 * Exportação Globus — contas a pagar (duas linhas com layout `txtSalesPayable`).
 * Este arquivo duplica propositalmente ZIP, agregação e motor de layout usados em vendas,
 * para manter o fluxo independente.
 */
export const exportSalesAccountsPayableToTXTNew = async (
  apiData: SalesExportAccountsPayableTxtResponseType,
  routes: {
    id: number;
    value: string;
    areaId: number;
  }[],
  dispatch: ThunkDispatch<EmptyObject & PersistPartial, undefined, AnyAction> &
    Dispatch<AnyAction>,
  worker: (
    apiData: SalesExportAccountsPayableTxtResponseType,
    routes: {
      id: number;
      value: string;
      areaId: number;
    }[],
    origin: string,
  ) => Promise<HandledGlobusSaleRow[]>,
): Promise<void> => {
  /* Mesmo objeto que vem de res.payload.data no toolbar — 1º argumento de cada chamada ao worker. */
  console.log('[Globus contas a pagar] entrada p/ worker', {
    apiData,
    routes,
  });
  return exportGlobusAccountsPayableTxtZip(
    apiData,
    routes,
    dispatch,
    worker,
    'relatório-contas-a-pagar',
  );
};

export const handleApiDataSalesAccountsPayableToTXTNew = (
  apiData: SalesExportAccountsPayableTxtResponseType,
  routes: {
    id: number;
    value: string;
    areaId: number;
  }[],
  origin: string,
) => {
  const sales: HandledGlobusSaleRow[] = [];

  let data = [];
  if (origin === 'driver') {
    data = apiData.salesDriver;
  } else {
    data = apiData.salesUser;
  }
  // eslint-disable-next-line lodash/prefer-lodash-method
  data.map((item) => {
    // eslint-disable-next-line lodash/prefer-lodash-method
    const route = routes.find((auxItem) => {
      return auxItem.id === item.routeId;
    });
    const area =
      route &&
      // eslint-disable-next-line lodash/prefer-lodash-method
      apiData.areas.find((auxItem) => {
        return auxItem.id === route.areaId;
      });
    if (area) {
      // eslint-disable-next-line lodash/prefer-lodash-method
      const saleIdx = sales.findIndex((auxItem) => {
        return (
          // eslint-disable-next-line lodash/prefer-matches
          auxItem.areaId === area.id &&
          auxItem.paymentMethod === item.paymentMethod &&
          auxItem.registerDate === item.registerDate
        );
      });
      if (saleIdx === -1) {
        sales.push({
          paymentMethod: item.paymentMethod,
          registerDate: item.registerDate,
          sumPrice: item.sumPrice,
          areaId: area.id,
          areaName: area.name,
          areaColor: area.color,
          areaClientNumber: area.clientNumber,
          areaTypeRecipe: area.typeRecipe,
          areaCostCenter: area.costCenter,
          origin: origin,
        });
      } else {
        sales[saleIdx].sumPrice += item.sumPrice;
      }
    }
  });

  return sales;
};

const createSalesExportTxtNewLine = (
  document: {
    field: string;
    start: number;
    size: number;
    type: string;
    mandatory: string;
  }[],
  sale: HandledGlobusSaleRow,
) => {
  let txt = '';
  map(document, (item) => {
    if (item.mandatory === 'S') {
      switch (item.field) {
        case 'Tipo': {
          txt = txt + 'D,';
          break;
        }
        case 'NrForn': {
          txt =
            txt +
            handleNormalizeTxtNewFieldForReport(item, sale.areaClientNumber);
          break;
        }
        case 'Serie': {
          txt = txt + handleNormalizeTxtNewFieldForReport(item, '0');
          break;
        }
        case 'Documento': {
          switch (sale.paymentMethod) {
            case 'CREDITO': {
              const txtAux = sale.origin === 'driver' ? 'CC' : 'CO';
              txt =
                txt +
                handleNormalizeTxtNewFieldForReport(
                  item,
                  moment(sale.registerDate).format('DDMMyyyy').toString() +
                    txtAux,
                );
              break;
            }
            case 'DEBITO': {
              txt =
                txt +
                handleNormalizeTxtNewFieldForReport(
                  item,
                  moment(sale.registerDate).format('DDMMyyyy').toString() +
                    'CD',
                );
              break;
            }
            case 'PIX': {
              const pixAux = sale.origin === 'driver' ? 'PS' : 'PX';
              txt =
                txt +
                handleNormalizeTxtNewFieldForReport(
                  item,
                  moment(sale.registerDate).format('DDMMyyyy').toString() +
                    pixAux,
                );
              break;
            }
            default: {
              txt =
                txt +
                handleNormalizeTxtNewFieldForReport(
                  item,
                  moment(sale.registerDate).format('DDMMyyyy').toString(),
                );
              break;
            }
          }
          break;
        }
        case 'Parcela': {
          txt = txt + handleNormalizeTxtNewFieldForReport(item, '1');
          break;
        }
        case 'CodTpDoc': {
          switch (sale.paymentMethod) {
            default: {
              txt = txt + handleNormalizeTxtNewFieldForReport(item, 'CTL');
              break;
            }
          }
          break;
        }
        case 'Emissao': {
          txt =
            txt +
            handleNormalizeTxtNewFieldForReport(
              item,
              moment(sale.registerDate).format('DD/MM/yyyy'),
            );
          break;
        }
        case 'Entrada': {
          txt =
            txt +
            handleNormalizeTxtNewFieldForReport(
              item,
              moment(sale.registerDate).format('DD/MM/yyyy'),
            );
          break;
        }
        case 'Vencimento': {
          txt =
            txt +
            handleNormalizeTxtNewFieldForReport(
              item,
              moment(sale.registerDate).format('DD/MM/yyyy'),
            );
          break;
        }
        case 'Quitado': {
          txt = txt + handleNormalizeTxtNewFieldForReport(item, 'N');
          break;
        }
        case 'Status': {
          txt = txt + handleNormalizeTxtNewFieldForReport(item, 'N');
          break;
        }
        default:
          break;
      }
    }

    let handledByAccountsPayableExtras = false;
    switch (item.field) {
      case 'Pagamento': {
        txt = txt + handleNormalizeTxtNewFieldForReport(item, ' / / ');
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'Desconto': {
        txt =
          txt + handleNormalizeTxtNewFieldForReport(item, '00000000.000000');
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'Acrescimo': {
        txt =
          txt + handleNormalizeTxtNewFieldForReport(item, '00000000.000000');
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'Observacao': {
        txt =
          txt +
          handleNormalizeTxtNewFieldForReport(
            item,
            '                                                                                                    ',
          );
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'Favorecido': {
        txt =
          txt +
          handleNormalizeTxtNewFieldForReport(
            item,
            '                                                  ',
          );
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'Indice': {
        txt = txt + handleNormalizeTxtNewFieldForReport(item, '   ');
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'VlrIRRFCPG': {
        txt = txt + handleNormalizeTxtNewFieldForReport(item, '0000000000.00');
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'VlrINSSCPG': {
        txt = txt + handleNormalizeTxtNewFieldForReport(item, '0000000000.00');
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'TipoPagtoCPG': {
        txt = txt + handleNormalizeTxtNewFieldForReport(item, '   ');
        handledByAccountsPayableExtras = true;
        break;
      }
      case 'StatusPE': {
        txt = txt + handleNormalizeTxtNewFieldForReport(item, '  ');
        handledByAccountsPayableExtras = true;
        break;
      }
      default:
        break;
    }

    if (item.mandatory !== 'S' && !handledByAccountsPayableExtras) {
      txt = txt + handleNormalizeTxtNewFieldForReport(item, '');
    }
  });
  /* handleNormalize acrescenta vírgula por campo; remover só a vírgula final.
     Concatenação crua sem vírgula no fim fazia substring apagar o último caractere (ex.: espaço). */
  return txt.endsWith(',') ? txt.slice(0, -1) : txt;
};

const handleNormalizeTxtNewFieldForReport = (
  field: {
    field: string;
    start: number;
    size: number;
    type: string;
    mandatory: string;
  },
  content: string,
) => {
  let limit = !content || content === '' ? 0 : content.length;

  const typeKey = String(field.type).toUpperCase();

  if (typeKey === 'C' || typeKey === 'DC' || typeKey === 'IC') {
    if (content === '') {
      while (limit < field.size) {
        content = ' ' + content;
        limit++;
      }
    } else {
      while (limit < field.size) {
        content = '0' + content;
        limit++;
      }
    }
  } else if (typeKey === 'N' || typeKey === 'DN' || typeKey === 'IN') {
    const sizeNormalized = replace(String(field.size), ',', '.');
    const sizeSplit = split(sizeNormalized, '.');
    const intWidth = parseInt(sizeSplit[0], 10) || 0;
    const fracWidth =
      sizeSplit.length > 1 && sizeSplit[1] !== undefined && sizeSplit[1] !== ''
        ? parseInt(sizeSplit[1], 10)
        : 0;

    const rawIn = replace(String(content), ',', '.').trim();
    const hadDecimalInInput =
      includes(rawIn, '.') || includes(String(content).trim(), ',');

    // Inicialização explícita: eslint-plugin-pii no-dob quebra sem node.init em VariableDeclarator (init null).
    let intPart = '';
    let fracPart = '';
    if (includes(rawIn, '.')) {
      const vp = split(rawIn, '.');
      intPart = vp[0] !== undefined ? vp[0] : '';
      fracPart = vp[1] !== undefined ? vp[1] : '';
    } else {
      intPart = rawIn;
      fracPart = '';
    }

    intPart = intPart.replace(/\D/g, '');
    fracPart = fracPart.replace(/\D/g, '');
    if (intPart === '' && (rawIn === '' || rawIn === '.')) {
      intPart = '0';
    }

    while (intPart.length < intWidth) {
      intPart = '0' + intPart;
    }
    if (intPart.length > intWidth) {
      intPart = intPart.slice(-intWidth);
    }

    if (fracWidth > 0) {
      while (fracPart.length < fracWidth) {
        fracPart = fracPart + '0';
      }
      if (fracPart.length > fracWidth) {
        fracPart = fracPart.slice(0, fracWidth);
      }
      content = `${intPart}.${fracPart}`;
    } else if (hadDecimalInInput) {
      content =
        fracPart !== '' || rawIn.endsWith('.')
          ? `${intPart}.${fracPart}`
          : `${intPart}`;
    } else {
      content = intPart;
    }
  } else if (typeKey === 'D' || typeKey === 'DD' || typeKey === 'ID') {
    if (content === '') {
      content = '  /  /    ';
    }
  }

  content = content + ',';

  return content;
};
